<template>
  <div>
    <template v-if="loading">
      <QualificationTransferTableSkeleton />
    </template>

    <template v-else>
      <div class="flex flex-col md:flex-row items-center gap-4 mb-6">
        <div class="w-[327px] shrink-0" style="height: 40px">
          <SearchInput
            v-model="searchText"
            width="100%"
            label="Buscar políticas"
            hide-details
            data-testid="search-policies-input"
            density="compact"
          />
        </div>

        <div @click.stop="showFilterModal = true">
          <SelectFilter
            label="Filtros"
            :icon="filterIcon"
            :options="[]"
            :is-open="false"
            :selectedValue="appliedCompanies"
            data-testid="filter-policies-button"
          />
        </div>

        <div class="ml-auto">
          <filled-button
            v-if="canCreate"
            :text="'Nova Política'"
            width="160px"
            height="40px"
            :prepend-icon="mdiPlus"
            class="!bg-primary30"
            data-testid="create-policy-button"
            @click="showCreateModal = true"
          />
        </div>
      </div>

      <div class="flex flex-col gap-6">
        <div class="overflow-x-auto rounded-lg shadow-sm border border-gray-200 min-h-[300px]">
          <TableModular
            v-if="filteredPolicies.length > 0"
            :rows="filteredPolicies"
            :columns="columns"
            actions-label="AÇÕES"
          >
            <template #column-id="{ value }">
              <LabelBadge :label="'#' + value" color="gray" :icon="null" size="small" />
            </template>

            <template #actions="{ row }">
              <v-icon
                :icon="mdiPencilOutline"
                color="primary"
                size="small"
                class="cursor-pointer"
                data-testid="edit-policy-button"
                @click="handleEditPolicy(row.id)"
              />
              <v-icon
                v-if="canCreate"
                :icon="mdiContentCopy"
                color="primary"
                size="small"
                class="cursor-pointer"
                data-testid="duplicate-policy-button"
                @click="handleDuplicatePolicy(row.id)"
              />
              <DsSwitch
                :model-value="row.isActive"
                size="small"
                :disabled="!canToggle"
                data-testid="toggle-policy-switch"
                @update:model-value="(val: boolean) => handleToggleActive(row.id, val)"
              />
            </template>
          </TableModular>

          <EmptyList v-else title="Nenhuma política encontrada" class="py-12" />
        </div>
      </div>
    </template>

    <FilterModal
      v-if="showFilterModal"
      :modal-active="showFilterModal"
      :applied-filters="appliedFilters"
      @close="showFilterModal = false"
      @apply-filters="handleApplyFilters"
    />

    <CreatePolicyModal
      v-if="showCreateModal"
      :modal-active="showCreateModal"
      :loading="isCreating"
      @close="showCreateModal = false"
      @submit="handleCreatePolicy"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, defineAsyncComponent } from 'vue'
import { useRouter } from 'vue-router'

import { mdiPlus, mdiPencilOutline, mdiContentCopy } from '@mdi/js'
import { useToast } from 'vue-toastification'

import { useErrorToast } from '@/composables/useErrorToast'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import PATHS from '@/router/paths'
import Network from '@/Network'
import type { QualificationDocumentalPolicyItem } from '@/Network/Types/Responses/QualificationDocumentalResponses'

import QualificationTransferTableSkeleton from '@/components/designSystem/Skeleton/QualificationTransferTableSkeleton.vue'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import TableModular from '@/components/designSystem/Table/TableModular/TableModular.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import DsSwitch from '@/components/designSystem/Switch/index.vue'
import EmptyList from '@/components/EmptyList.vue'
import FilterModal from './components/FilterModal.vue'
import type { QualificationDocumentalFilters } from './components/FilterModal.vue'
import CreatePolicyModal from '@/modules/company/Policy/components/CreatePolicyModal.vue'
import type { CreatePolicyFormData } from '@/modules/company/Policy/components/CreatePolicyModal.vue'
import { useQualificationPolicyPermissions } from '@/modules/company/Policy/composables/useQualificationPolicyPermissions'

const filterIcon = defineAsyncComponent(() => import('@/assets/icons/filter.vue'))

const router = useRouter()
const toast = useToast()
const { notifyError } = useErrorToast()
const headerStore = useHeaderStore()
const { canCreate, canToggle } = useQualificationPolicyPermissions('documental')
const searchText = ref('')
const showFilterModal = ref(false)
const showCreateModal = ref(false)
const isCreating = ref(false)
const loading = ref(false)
const policies = ref<QualificationDocumentalPolicyItem[]>([])
const appliedFilters = ref<QualificationDocumentalFilters>({ companies: [] })

const appliedCompanies = computed(() => appliedFilters.value.companies)

function handleEditPolicy(policyId: number) {
  router.push(`${PATHS.MANAGE_QUALIFICATION_DOC}/${policyId}`)
}

function handleApplyFilters(filters: QualificationDocumentalFilters) {
  appliedFilters.value = filters
}

async function handleCreatePolicy(formData: CreatePolicyFormData) {
  try {
    isCreating.value = true
    await Network.qualificationDocumentalPolicy.create({
      name: formData.name,
      companyId: Number(formData.companyId)
    })
    toast.success('Política criada com sucesso!')
    // Só fecha no sucesso: em erro a modal fica aberta para o usuário corrigir
    // (ex.: 409 de nome duplicado) sem redigitar o formulário.
    showCreateModal.value = false
    await fetchPolicies()
  } catch (error) {
    console.error(error)
    notifyError(error, 'Erro ao criar política. Verifique os dados.')
  } finally {
    isCreating.value = false
  }
}

async function handleToggleActive(policyId: number, value: boolean) {
  const index = policies.value.findIndex((p) => p.id === policyId)
  if (index === -1) return
  policies.value[index] = { ...policies.value[index], isActive: value }
  try {
    await Network.qualificationDocumentalPolicy.toggleActive(policyId, { isActive: value })
    toast.success(value ? 'Política ativada com sucesso!' : 'Política desativada com sucesso!')
  } catch (error) {
    policies.value[index] = { ...policies.value[index], isActive: !value }
    notifyError(error, 'Erro ao alterar status da política.')
  }
}

async function handleDuplicatePolicy(policyId: number) {
  try {
    await Network.qualificationDocumentalPolicy.duplicate(policyId)
    toast.success('Política duplicada com sucesso!')
    await fetchPolicies()
  } catch (error) {
    notifyError(error, 'Erro ao duplicar política.')
  }
}

const columns = [
  { key: 'id', label: 'ID', width: 'auto' },
  { key: 'policyName', label: 'NOME DA POLÍTICA' },
  { key: 'company', label: 'EMPRESA' },
  { key: 'createdBy', label: 'CRIADO POR' }
]

const filteredPolicies = computed(() => {
  if (!searchText.value) return policies.value
  const query = searchText.value.toLowerCase()
  return policies.value.filter((p) => p.policyName.toLowerCase().includes(query))
})

async function fetchPolicies() {
  try {
    loading.value = true
    const response = await Network.qualificationDocumentalPolicy.list({ limit: 1000 })
    policies.value = response.data
  } catch (error) {
    console.error(error)
    notifyError(error, 'Erro ao carregar políticas de qualificação documental.')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  headerStore.setTitle(
    'Qualificação Documental',
    'Análise de documentação para alocação e faturamento',
    {
      label: 'Políticas',
      route: PATHS.POLICIES_HOME
    }
  )
  fetchPolicies()
})
</script>
